// ============================================================
// ANSICHTSSACHE-AGENT – Auswahlfeld
// ============================================================
// Ein Auswahlfeld der Anwendung, kein `select` des Betriebs-
// systems. Der Grund steht im Screenshot, der dieses Bauteil
// ausgeloest hat: 180 Projekte in einer grauen Systemliste, ohne
// Suche, ohne zweite Zeile, in einer Schrift, die nicht zur
// Anwendung gehoert. Wer „Betreuung“ sucht, scrollt.
//
// Deshalb:
//   • Aufklapper in der eigenen Gestaltung (Glas, Rundung,
//     Schatten wie bei jeder anderen schwebenden Flaeche),
//   • ab `SEARCH_THRESHOLD` Eintraegen ein Suchfeld obenauf —
//     unscharf gegen Gross-/Kleinschreibung und Umlaute, mehrere
//     Woerter werden alle verlangt („mey web“ findet „Meyer
//     Website“),
//   • Haken an der gewaehlten Zeile, optionale zweite Zeile
//     (`hint`) fuer Kunde, Nummer oder E-Mail,
//   • vollstaendige Tastaturbedienung: Pfeile, Pos1/Ende,
//     Eingabe, Escape; ohne Suchfeld zusaetzlich Anspringen per
//     Anfangsbuchstabe.
//
// Fuer Formulare liegt ein echtes `select` unsichtbar ueber dem
// Feld. Das ist kein Zierrat: nur so kommt der Wert in die
// `FormData` der Server Action, nur so greift `required`, und
// der Hinweis des Browsers steht an der richtigen Stelle.
// ============================================================

"use client";

import { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { AlertCircle, Check, ChevronDown, Plus, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { FieldLabel } from "./field-label";
import { noAutocorrect } from "./no-autocorrect";
import { Tag } from "./tag";

/**
 * Ab so vielen Eintraegen bekommt die Liste ein Suchfeld. Drei
 * Zeilen durchsucht niemand, die liest man; ab etwa acht kippt
 * es. Mit `searchable` laesst sich beides erzwingen.
 */
const SEARCH_THRESHOLD = 8;

/**
 * Der Wert der Zeile „… anlegen". Er steht nie in `options` und
 * geht nie nach draussen — `choose` faengt ihn ab und reicht
 * statt seiner das Getippte an `creatable.onCreate`.
 */
const ANLEGEN = "__anlegen__";

export interface SelectOption {
  value: string;
  label: string;
  /** Zweite, leisere Zeile im Aufklapper. Wird mitgesucht. */
  hint?: string;
  disabled?: boolean;
  /**
   * Überschrift der Gruppe, zu der die Zeile gehört. Sie steht über
   * der ersten Zeile jeder Gruppe — die Einträge kommen deshalb
   * schon nach Gruppen geordnet. Gesucht wird nur in der Zeile.
   */
  group?: string;
}

export interface SelectProps {
  options: SelectOption[];
  /** Gesteuert. Ohne `value` regelt das Feld sich selbst (`defaultValue`). */
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  /** Ohne `name` nimmt kein Formular den Wert auf. */
  name?: string;
  id?: string;
  label?: string;
  /** Steht da, solange nichts gewaehlt ist — kein Listeneintrag. */
  placeholder?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  /** Suchfeld erzwingen oder unterdruecken; sonst ab `SEARCH_THRESHOLD`. */
  searchable?: boolean;
  /**
   * Was nicht in der Liste steht, darf getippt werden.
   *
   * Dann traegt die Liste immer ein Suchfeld — es ist hier nicht
   * nur die Suche, sondern die Stelle, an der man schreibt. Was
   * keinen Treffer hat, bekommt unten die Zeile „… anlegen";
   * geklickt oder mit der Eingabetaste bestaetigt ruft sie
   * `onCreate` mit dem Getippten.
   */
  creatable?: {
    onCreate: (getippt: string) => void;
    /** Beschriftung der Zeile. Vorgabe: „…“ anlegen. */
    label?: (getippt: string) => string;
  };
  /**
   * Eintraege als Marke zeichnen statt als Zeile Text — fuer
   * Namen, die jemand vergeben hat (Kostenarten), nicht fuer
   * Kunden, Projekte oder Zustaende.
   */
  tags?: boolean;
  searchPlaceholder?: string;
  emptyLabel?: string;
  /** Auf den Ausloeser — hier steht die Hoehe, wenn sie nicht `h-10` ist. */
  className?: string;
  /** Auf die Huelle aus Beschriftung, Feld und Fehlertext. */
  wrapperClassName?: string;
  contentClassName?: string;
  align?: "start" | "center" | "end";
  "aria-label"?: string;
}

/** Ohne Akzente, ohne Grossschreibung — so wird gesucht. */
function normalize(text: string) {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

export function Select({
  options,
  value,
  defaultValue,
  onChange,
  name,
  id,
  label,
  placeholder = "Bitte wählen…",
  error,
  hint,
  required,
  disabled,
  searchable,
  creatable,
  tags,
  searchPlaceholder = "Suchen…",
  emptyLabel = "Nichts gefunden",
  className,
  wrapperClassName,
  contentClassName,
  align = "start",
  "aria-label": ariaLabel,
}: SelectProps) {
  const generatedId = useId();
  const fieldId = id ?? name ?? generatedId;
  const listId = `${fieldId}-list`;

  const isControlled = value !== undefined;
  const [innerValue, setInnerValue] = useState(defaultValue ?? "");
  const currentValue = isControlled ? value : innerValue;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const proxyRef = useRef<HTMLSelectElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const typed = useRef({ text: "", at: 0 });

  const selected = options.find((option) => option.value === currentValue);
  // Wo getippt werden darf, ist das Suchfeld kein Zubehoer,
  // sondern die Eingabe selbst — es steht dann immer da, auch
  // bei drei Eintraegen.
  const showSearch = searchable ?? (creatable ? true : options.length >= SEARCH_THRESHOLD);

  const visible = useMemo(() => {
    const terms = normalize(query).split(/\s+/).filter(Boolean);
    const getroffen =
      terms.length === 0
        ? options
        : options.filter((option) => {
            const haystack = normalize(`${option.label} ${option.hint ?? ""}`);
            return terms.every((term) => haystack.includes(term));
          });

    // Die Anlegen-Zeile nur, wenn es das Getippte noch nicht
    // gibt. Verglichen wird gegen ALLE Eintraege und nicht nur
    // gegen die gefilterten: „Strom" mit einem Leerzeichen
    // dahinter filtert zwar, ist aber derselbe Name.
    const getippt = query.trim();
    if (!creatable || getippt === "") return getroffen;
    const gibtEsSchon = options.some((option) => normalize(option.label) === normalize(getippt));
    if (gibtEsSchon) return getroffen;
    return [
      ...getroffen,
      {
        value: ANLEGEN,
        label: creatable.label?.(getippt) ?? `„${getippt}“ anlegen`,
      },
    ];
  }, [options, query, creatable]);

  // `form.reset()` nach dem Absenden stellt das verdeckte
  // `select` zurueck — der sichtbare Ausloeser wuesste sonst
  // nichts davon und zeigte weiter den alten Namen.
  useEffect(() => {
    const form = proxyRef.current?.form;
    if (!form || isControlled) return;
    const reset = () => setInnerValue(defaultValue ?? "");
    form.addEventListener("reset", reset);
    return () => form.removeEventListener("reset", reset);
  }, [isControlled, defaultValue]);

  // Escape faengt die Liste selbst ab — und zwar am Dokument in
  // der Auffangphase.
  //
  // Der naheliegende Weg (`onKeyDown` am Aufklapper mit
  // `stopPropagation`) traegt hier nicht: Radix schliesst die
  // Ebene schon in der Auffangphase, React nimmt das Bauteil
  // daraufhin aus dem Baum, und der Handler am portalierten
  // Inhalt kommt gar nicht mehr an die Reihe. Die Taste laeuft
  // weiter bis zum Dokument — wo Dialog und Seitenblatt lauschen
  // und mit der Liste das ganze Fenster zumachen.
  useEffect(() => {
    if (!open) return;
    function catchEscape(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      setOpen(false);
    }
    document.addEventListener("keydown", catchEscape, true);
    return () => document.removeEventListener("keydown", catchEscape, true);
  }, [open]);

  // Die markierte Zeile muss sichtbar bleiben — beim Oeffnen
  // einer langen Liste ebenso wie beim Wandern mit den Pfeilen.
  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (next) {
      setQuery("");
      const index = options.findIndex((option) => option.value === currentValue);
      setActiveIndex(index < 0 ? 0 : index);
    }
  }

  const choose = useCallback(
    (option: SelectOption) => {
      if (option.disabled) return;
      if (option.value === ANLEGEN) {
        const getippt = query.trim();
        setOpen(false);
        if (getippt !== "") creatable?.onCreate(getippt);
        return;
      }
      if (!isControlled) setInnerValue(option.value);
      onChange?.(option.value);
      setOpen(false);
    },
    [isControlled, onChange, creatable, query],
  );

  /** Ueberspringt gesperrte Zeilen und laeuft am Ende um. */
  function move(step: number) {
    if (visible.length === 0) return;
    let next = activeIndex;
    for (let i = 0; i < visible.length; i++) {
      next = (next + step + visible.length) % visible.length;
      if (!visible[next]?.disabled) break;
    }
    setActiveIndex(next);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      move(1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      move(-1);
    } else if (e.key === "Home") {
      e.preventDefault();
      setActiveIndex(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setActiveIndex(visible.length - 1);
    } else if (e.key === "Enter") {
      const option = visible[activeIndex];
      // Auch ohne Treffer anhalten: sonst schickt die Eingabe
      // das Formular ab, waehrend die Liste noch offen steht.
      e.preventDefault();
      if (option) choose(option);
    } else if (e.key === "Tab") {
      setOpen(false);
    } else if (!showSearch && e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      // Kurze Liste, kein Suchfeld: Anfangsbuchstaben springen
      // an, wie man es vom Betriebssystem kennt.
      const now = Date.now();
      typed.current.text = now - typed.current.at > 700 ? e.key : typed.current.text + e.key;
      typed.current.at = now;
      const needle = normalize(typed.current.text);
      const index = visible.findIndex((option) => normalize(option.label).startsWith(needle));
      if (index >= 0) setActiveIndex(index);
    }
  }

  return (
    <div className={cn("space-y-1.5", wrapperClassName)}>
      {label && <FieldLabel htmlFor={fieldId}>{label}</FieldLabel>}

      <div className="relative">
        {name && (
          <select
            ref={proxyRef}
            name={name}
            value={currentValue}
            onChange={() => {}}
            required={required}
            disabled={disabled}
            tabIndex={-1}
            aria-hidden
            className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
          >
            <option value="" />
            {options
              .filter((option) => option.value !== "")
              .map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
          </select>
        )}

        <Popover.Root open={open} onOpenChange={handleOpenChange}>
          <Popover.Trigger asChild>
            <button
              type="button"
              id={fieldId}
              role="combobox"
              aria-expanded={open}
              aria-haspopup="listbox"
              aria-controls={open ? listId : undefined}
              aria-label={ariaLabel}
              aria-invalid={error ? true : undefined}
              disabled={disabled}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                  e.preventDefault();
                  handleOpenChange(true);
                }
              }}
              className={cn(
                "bg-bg-primary text-body-sm text-text-primary flex h-10 w-full cursor-pointer items-center gap-2 rounded-lg border px-3 text-left",
                // Nur `box-shadow` und `border-color` bewegen sich —
                // wie beim Eingabefeld daneben.
                "duration-fast ease-standard transition-[box-shadow,border-color]",
                "hover:border-border-strong",
                "focus:border-accent/40 focus:ring-2 focus:outline-none",
                "disabled:hover:border-border disabled:cursor-not-allowed disabled:opacity-40",
                error ? "border-danger focus:ring-danger/30" : "border-border focus:ring-accent/30",
                className,
              )}
            >
              <span className={cn("min-w-0 flex-1 truncate", !selected && "text-text-tertiary")}>
                {selected ? tags ? <Tag>{selected.label}</Tag> : selected.label : placeholder}
              </span>
              <ChevronDown className="text-text-tertiary h-4 w-4 shrink-0" aria-hidden />
            </button>
          </Popover.Trigger>

          <Popover.Portal>
            <Popover.Content
              align={align}
              sideOffset={6}
              collisionPadding={12}
              onOpenAutoFocus={(e) => {
                e.preventDefault();
                (showSearch ? searchRef : listRef).current?.focus();
              }}
              onKeyDown={handleKeyDown}
              className={cn(
                // Deckend, nicht aus Glas: hinter einer langen
                // Liste steht Text, und durch Milchglas gelesen
                // wird aus „Betreuung“ und dem Tabellenkopf
                // darunter ein Muster. Dieselbe Flaeche wie beim
                // Menue — bg-bg-primary mit `shadow-dropdown`.
                "border-border bg-bg-primary shadow-dropdown z-50 overflow-hidden rounded-xl border",
                // So breit wie das Feld, aber nie breiter als der
                // Bildschirm: lange Projektnamen sollen lesbar
                // sein, ohne dass die Liste hinauslaeuft.
                "w-max max-w-[min(32rem,calc(100vw-1.5rem))] min-w-[var(--radix-popover-trigger-width)]",
                contentClassName,
              )}
            >
              {showSearch && (
                <div className="border-border-subtle relative border-b">
                  <Search
                    className="text-text-tertiary pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2"
                    aria-hidden
                  />
                  {/* Suchfeld, kein Anmeldefeld: ohne type="search" hält Safari es
                      (zumal mit „E-Mail“ im Platzhalter) für den Benutzernamen und legt
                      seine Passwortliste über die Auswahl. */}
                  <input
                    ref={searchRef}
                    type="search"
                    {...noAutocorrect}
                    data-1p-ignore
                    data-lpignore="true"
                    data-bwignore
                    data-form-type="other"
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setActiveIndex(0);
                    }}
                    placeholder={searchPlaceholder}
                    aria-label={searchPlaceholder}
                    aria-controls={listId}
                    aria-activedescendant={
                      visible[activeIndex] ? `${listId}-${activeIndex}` : undefined
                    }
                    className="text-body-sm text-text-primary placeholder:text-text-tertiary h-10 w-full appearance-none bg-transparent pr-3 pl-9 focus:outline-none [&::-webkit-search-cancel-button]:hidden"
                  />
                </div>
              )}

              <div
                id={listId}
                ref={listRef}
                role="listbox"
                tabIndex={-1}
                aria-label={label ?? ariaLabel}
                className="max-h-[min(18rem,var(--radix-popover-content-available-height,18rem))] overflow-y-auto overscroll-contain p-1 focus:outline-none"
              >
                {visible.length === 0 ? (
                  <p className="text-caption text-text-tertiary px-2 py-3">{emptyLabel}</p>
                ) : (
                  visible.map((option, index) => (
                    <Fragment key={option.value}>
                      {option.group && option.group !== visible[index - 1]?.group && (
                        // Die Überschrift steht an der Textkante der
                        // Zeilen, nicht an der des Hakens.
                        <div
                          role="presentation"
                          className={cn(
                            "text-overline text-text-tertiary pt-2 pr-2 pb-1 pl-8",
                            index > 0 && "border-border-subtle mt-1 border-t",
                          )}
                        >
                          {option.group}
                        </div>
                      )}
                      <button
                        id={`${listId}-${index}`}
                        type="button"
                        role="option"
                        aria-selected={option.value === currentValue}
                        data-index={index}
                        tabIndex={-1}
                        disabled={option.disabled}
                        onMouseEnter={() => setActiveIndex(index)}
                        // `onMouseDown` statt `onClick`: der Klick
                        // kaeme erst nach dem Fokuswechsel an, und
                        // die Liste ist dann schon zu.
                        onMouseDown={(e) => {
                          e.preventDefault();
                          choose(option);
                        }}
                        className={cn(
                          // Der Haken steht in der Steuerspalte, nicht
                          // in der Zeile — sonst schoebe er den Text
                          // vor sich her und die Textkante saesse
                          // anders als im Suchfeld darueber.
                          "duration-fast relative flex w-full items-center rounded-md py-1.5 pr-2 pl-8 text-left transition-colors",
                          index === activeIndex && "bg-bg-secondary",
                          option.disabled && "cursor-not-allowed opacity-40",
                        )}
                      >
                        {option.value === ANLEGEN ? (
                          // Das Pluszeichen steht in derselben
                          // Steuerspalte wie der Haken: die Zeile
                          // legt an, statt zu waehlen, und sagt es
                          // an der Stelle, an der man hinsieht.
                          <Plus
                            className="text-text-tertiary absolute top-1/2 left-2 h-3.5 w-3.5 -translate-y-1/2"
                            aria-hidden
                          />
                        ) : (
                          <Check
                            className={cn(
                              "absolute top-1/2 left-2 h-3.5 w-3.5 -translate-y-1/2",
                              option.value === currentValue ? "text-accent" : "text-transparent",
                            )}
                            aria-hidden
                          />
                        )}
                        <span className="min-w-0 flex-1">
                          {/* Der leere Eintrag („Ohne Kostenart")
                            bleibt Text: er ist kein Name,
                            sondern der Weg heraus. */}
                          {tags && option.value !== ANLEGEN && option.value !== "" ? (
                            <Tag>{option.label}</Tag>
                          ) : (
                            <span className="text-body-sm text-text-primary block truncate">
                              {option.label}
                            </span>
                          )}
                          {option.hint && (
                            <span className="text-caption text-text-tertiary block truncate">
                              {option.hint}
                            </span>
                          )}
                        </span>
                      </button>
                    </Fragment>
                  ))
                )}
              </div>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      </div>

      {error && (
        <p className="text-caption text-danger flex items-start gap-1.5" role="alert">
          <AlertCircle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          {error}
        </p>
      )}
      {hint && !error && <p className="text-caption text-text-tertiary">{hint}</p>}
    </div>
  );
}
